import { useCallback, useEffect, useState } from 'react'

import { getMatterWhereYouLeftOff } from '@/matter/where-you-left-off/fetch/get-matter-where-you-left-off'
import type { MatterWhereYouLeftOffState } from '@/matter/where-you-left-off/schemas'

type MatterResumeRequestState = {
  matterId: string
  state: MatterWhereYouLeftOffState | null
  error: unknown | null
  isLoading: boolean
}

export function useMatterWhereYouLeftOff(matterId: string) {
  const [requestVersion, setRequestVersion] = useState(0)
  const [requestState, setRequestState] = useState<MatterResumeRequestState>({
    matterId,
    state: null,
    error: null,
    isLoading: true,
  })

  useEffect(() => {
    const controller = new AbortController()
    setRequestState((current) => ({
      matterId,
      state: current.matterId === matterId ? current.state : null,
      error: null,
      isLoading: true,
    }))

    void getMatterWhereYouLeftOff(matterId, controller.signal)
      .then((state) => {
        if (controller.signal.aborted) return
        setRequestState({ matterId, state, error: null, isLoading: false })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setRequestState({ matterId, state: null, error, isLoading: false })
      })

    return () => controller.abort()
  }, [matterId, requestVersion])

  const refetch = useCallback(() => setRequestVersion((version) => version + 1), [])
  const visibleState = requestState.matterId === matterId ? requestState : null

  return {
    state: visibleState?.state ?? null,
    error: visibleState?.error ?? null,
    isLoading: visibleState?.isLoading ?? true,
    refetch,
  }
}
